import React, { useState, useEffect } from 'react';
import Loader from '../../components/loader/loader';
import Image from '../assets/4949455_19732 1.png';
import LogoImage from '../assets/logo_tapvox.png';
import swipeLogoImage from '../assets/swipedesk.png';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'
import {
  loginemployee,
  startMfaSetup,
  verifyMfaSetup,
  verifyMfaLogin,
  verifyMfaRecoveryCode,
} from '../../features/employeeSlice';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'
import { toast } from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { url } from '../../services/api'
import { ThreeDots } from 'react-loader-spinner'

const LOGIN_STEP = 'LOGIN';
const MFA_SETUP_STEP = 'MFA_SETUP';
const MFA_VERIFY_STEP = 'MFA_VERIFY';
const MFA_RECOVERY_STEP = 'MFA_RECOVERY';
const MFA_RECOVERY_CODES_STEP = 'MFA_RECOVERY_CODES';

// login page
export default function Login() {
  const logoPath = process.env.REACT_APP_NAME === 'TAPDESK' ? LogoImage : process.env.REACT_APP_NAME === 'SWIPEDESK' ? swipeLogoImage : LogoImage;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee);

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", role: "" })
  const [loading, setLoading] = useState(false)

  const [step, setStep] = useState(LOGIN_STEP);
  const [setupToken, setSetupToken] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [showingRecoveryCodes, setShowingRecoveryCodes] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading2, setLoading2] = useState(false);

  useEffect(() => {
    if (employee._id && !showingRecoveryCodes) {
      navigate("/dashboard");
    }
  }, [navigate, employee._id, showingRecoveryCodes]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [])

  const changeHandler = (e) => {
    setFormData((prevFormData) => {
      return {
        ...prevFormData,
        [e.target.name]: e.target.value
      }
    })
  }

  const resetMfaFlow = () => {
    setStep(LOGIN_STEP);
    setSetupToken('');
    setMfaToken('');
    setQrCodeDataUrl('');
    setManualKey('');
    setMfaCode('');
    setRecoveryCode('');
    setRecoveryCodes([]);
    setShowingRecoveryCodes(false);
    setSetupLoading(false);
  };

  const isValidSixDigitCode = (code) => /^\d{6}$/.test(String(code || '').trim());

  const formatRecoveryInput = (value) => String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 12)
    .replace(/(.{4})/g, '$1-')
    .replace(/-$/, '');

  const copyRecoveryCodes = async () => {
    const text = recoveryCodes.join('\n');

    try {
      await navigator.clipboard.writeText(text);
      toast.success('Recovery codes copied');
    } catch (error) {
      toast.error('Could not copy recovery codes. Please copy them manually.');
    }
  };

  const continueAfterSavingRecoveryCodes = () => {
    setShowingRecoveryCodes(false);
    navigate('/dashboard');
  };

  const handleStartMfaSetup = async (token) => {
    setSetupLoading(true);
    setQrCodeDataUrl('');
    setManualKey('');

    const setupAction = await dispatch(startMfaSetup({ setupToken: token }));

    if (startMfaSetup.fulfilled.match(setupAction)) {
      setQrCodeDataUrl(setupAction.payload?.qrCodeDataUrl || '');
      setManualKey(setupAction.payload?.manualKey || '');
    }

    setSetupLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.role.length === 0) {
      toast.error("Select login role");
      setLoading(false);
      return;
    }

    if (formData.email.length === 0) {
      toast.error("Enter a valid Email");
      setLoading(false);
      return;
    }

    if (formData.password.length === 0) {
      toast.error("Enter a valid Password");
      setLoading(false);
      return;
    }

    const action = await dispatch(loginemployee(formData));

    if (loginemployee.fulfilled.match(action)) {
      const data = action.payload;

      if (data?.mfaSetupRequired && data?.setupToken) {
        setSetupToken(data.setupToken);
        setMfaCode('');
        setStep(MFA_SETUP_STEP);
        await handleStartMfaSetup(data.setupToken);
      } else if (data?.mfaRequired && data?.mfaToken) {
        setMfaToken(data.mfaToken);
        setMfaCode('');
        setStep(MFA_VERIFY_STEP);
      }
    }

    setLoading(false);
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();

    if (!setupToken) {
      toast.error("MFA setup token missing. Please login again.");
      resetMfaFlow();
      return;
    }

    if (!isValidSixDigitCode(mfaCode)) {
      toast.error("Enter a valid 6-digit authenticator code");
      return;
    }

    setLoading(true);
    setShowingRecoveryCodes(true);
    const action = await dispatch(verifyMfaSetup({ setupToken, code: mfaCode.trim() }));

    if (verifyMfaSetup.fulfilled.match(action) && action.payload?.token) {
      const codes = Array.isArray(action.payload?.recoveryCodes) ? action.payload.recoveryCodes : [];

      if (codes.length > 0) {
        setRecoveryCodes(codes);
        setStep(MFA_RECOVERY_CODES_STEP);
      } else {
        setShowingRecoveryCodes(false);
        navigate('/dashboard');
      }
    } else {
      setShowingRecoveryCodes(false);
    }

    setLoading(false);
  };

  const handleVerifyLogin = async (e) => {
    e.preventDefault();

    if (!mfaToken) {
      toast.error("MFA login token missing. Please login again.");
      resetMfaFlow();
      return;
    }

    if (!isValidSixDigitCode(mfaCode)) {
      toast.error("Enter a valid 6-digit authenticator code");
      return;
    }

    setLoading(true);
    const action = await dispatch(verifyMfaLogin({ mfaToken, code: mfaCode.trim() }));

    if (verifyMfaLogin.fulfilled.match(action) && action.payload?.token) {
      navigate('/dashboard');
    }

    setLoading(false);
  };

  const handleVerifyRecoveryLogin = async (e) => {
    e.preventDefault();

    if (!mfaToken) {
      toast.error("MFA login token missing. Please login again.");
      resetMfaFlow();
      return;
    }

    if (!recoveryCode || recoveryCode.replace(/[^A-Z0-9]/gi, '').length !== 12) {
      toast.error("Enter a valid recovery code");
      return;
    }

    setLoading(true);
    const action = await dispatch(verifyMfaRecoveryCode({ mfaToken, recoveryCode }));

    if (verifyMfaRecoveryCode.fulfilled.match(action) && action.payload?.token) {
      navigate('/dashboard');
    }

    setLoading(false);
  };

  const handleFormSubmit = (e) => {
    if (step === MFA_SETUP_STEP) {
      return handleVerifySetup(e);
    }

    if (step === MFA_VERIFY_STEP) {
      return handleVerifyLogin(e);
    }

    if (step === MFA_RECOVERY_STEP) {
      return handleVerifyRecoveryLogin(e);
    }

    if (step === MFA_RECOVERY_CODES_STEP) {
      e.preventDefault();
      return continueAfterSavingRecoveryCodes();
    }

    return handleSubmit(e);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason && reason === "backdropClick")
      return;
    setOpen(false);
    setEmail('');
    setLoading(false);
  };

  const handleSendLink = async () => {
    setLoading2(true);
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailPattern.test(email)) {
      toast.error("Valid Email Required!");
      setLoading2(false);
      return false;
    }

    try {
      const res = await axios.post(`${url}/api/resetPasswordRequest`, {
        email: email,
      });

      if (res.status === 200) {
        setOpen(false);
        toast.success(`${res?.data?.message}`);
      }

    } catch (error) {
      toast.error(`Error : ${error?.response?.data?.message}`)
    }

    setLoading2(false)
  };

  const renderLoginFields = () => (
    <>
      <Typography variant="h5" component="h3" gutterBottom style={{ textAlign: 'center' }}>
        Login
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel id="role-label">Login As</InputLabel>
        <Select
          labelId="role-label"
          id="role"
          name="role"
          value={formData.role}
          onChange={changeHandler}
          label="Login As"
        >
          <MenuItem value=""><em>None</em></MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="Account Manager">Account Manager</MenuItem>
          <MenuItem value="NOC Manager">NOC Manager</MenuItem>
        </Select>
      </FormControl>
      <TextField
        margin="normal"
        fullWidth
        id="email"
        label="Username"
        name="email"
        autoComplete="email"
        value={formData.email}
        onChange={changeHandler}
        variant="outlined"
      />
      <TextField
        margin="normal"
        fullWidth
        id="password"
        name="password"
        label="Password"
        type={showPassword ? "text" : "password"}
        value={formData.password}
        onChange={changeHandler}
        variant="outlined"
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <AiFillEye /> : <AiFillEyeInvisible />}
            </IconButton>
          ),
        }}
      />
      <div style={{ marginBottom: '16px' }}>
        <Button onClick={handleClickOpen} style={{ textTransform: 'none', color: '#1976d2' }}>Forgot Password?</Button>
      </div>
      <Button type="submit" variant="contained" color="primary" fullWidth style={{ height: '40px' }}>
        {loading ? <Loader /> : "SUBMIT"}
      </Button>
    </>
  );

  const renderMfaSetupFields = () => (
    <>
      <Typography variant="h5" component="h3" gutterBottom style={{ textAlign: 'center' }}>
        Security Setup Required
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Two-factor authentication is mandatory. Scan the QR code in Google Authenticator or Microsoft Authenticator, then enter the 6-digit code.
      </Alert>

      {setupLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <ThreeDots
            visible={true}
            height="45"
            width="45"
            color="#1976d2"
            radius="9"
            ariaLabel="three-dots-loading"
          />
        </Box>
      ) : (
        <>
          {qrCodeDataUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src={qrCodeDataUrl} alt="MFA QR Code" style={{ width: '210px', maxWidth: '100%', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '8px' }} />
            </Box>
          )}
          {manualKey && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, backgroundColor: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <Typography variant="caption" color="textSecondary">Manual setup key</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>{manualKey}</Typography>
            </Box>
          )}
        </>
      )}

      <TextField
        margin="normal"
        fullWidth
        id="mfa-setup-code"
        label="6-digit authenticator code"
        name="mfaCode"
        value={mfaCode}
        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        variant="outlined"
        inputProps={{ inputMode: 'numeric', maxLength: 6 }}
      />
      <Button type="submit" variant="contained" color="primary" fullWidth style={{ height: '40px', marginTop: '12px' }} disabled={loading || setupLoading || !qrCodeDataUrl}>
        {loading ? <Loader /> : "ENABLE MFA & CONTINUE"}
      </Button>
      <Button type="button" variant="text" fullWidth sx={{ mt: 1 }} onClick={resetMfaFlow}>
        Back to Login
      </Button>
    </>
  );

  const renderMfaVerifyFields = () => (
    <>
      <Typography variant="h5" component="h3" gutterBottom style={{ textAlign: 'center' }}>
        Verify MFA Code
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Enter the 6-digit code from your authenticator app. This browser will stay trusted for 7 days after successful verification.
      </Alert>
      <TextField
        margin="normal"
        fullWidth
        id="mfa-login-code"
        label="6-digit authenticator code"
        name="mfaCode"
        value={mfaCode}
        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        variant="outlined"
        inputProps={{ inputMode: 'numeric', maxLength: 6 }}
      />
      <Button type="submit" variant="contained" color="primary" fullWidth style={{ height: '40px', marginTop: '12px' }}>
        {loading ? <Loader /> : "VERIFY & LOGIN"}
      </Button>
      <Button
        type="button"
        variant="text"
        fullWidth
        sx={{ mt: 1 }}
        onClick={() => {
          setRecoveryCode('');
          setStep(MFA_RECOVERY_STEP);
        }}
      >
        Use recovery code instead
      </Button>
      <Button type="button" variant="text" fullWidth sx={{ mt: 1 }} onClick={resetMfaFlow}>
        Back to Login
      </Button>
    </>
  );

  const renderMfaRecoveryFields = () => (
    <>
      <Typography variant="h5" component="h3" gutterBottom style={{ textAlign: 'center' }}>
        Use Recovery Code
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Use one saved recovery code if you lost access to your authenticator app. Each code works only once.
      </Alert>
      <TextField
        margin="normal"
        fullWidth
        id="mfa-recovery-code"
        label="Recovery code"
        name="recoveryCode"
        value={recoveryCode}
        onChange={(e) => setRecoveryCode(formatRecoveryInput(e.target.value))}
        variant="outlined"
        inputProps={{ maxLength: 14 }}
      />
      <Button type="submit" variant="contained" color="primary" fullWidth style={{ height: '40px', marginTop: '12px' }}>
        {loading ? <Loader /> : "VERIFY RECOVERY CODE"}
      </Button>
      <Button
        type="button"
        variant="text"
        fullWidth
        sx={{ mt: 1 }}
        onClick={() => {
          setRecoveryCode('');
          setStep(MFA_VERIFY_STEP);
        }}
      >
        Back to authenticator code
      </Button>
      <Button type="button" variant="text" fullWidth sx={{ mt: 1 }} onClick={resetMfaFlow}>
        Back to Login
      </Button>
    </>
  );

  const renderRecoveryCodesFields = () => (
    <>
      <Typography variant="h5" component="h3" gutterBottom style={{ textAlign: 'center' }}>
        Save Recovery Codes
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Save these recovery codes now. They will not be shown again. Each code can be used only once if your phone is lost.
      </Alert>
      <Box sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: 2, backgroundColor: '#f8fafc' }}>
        {recoveryCodes.map((code) => (
          <Typography key={code} variant="body2" sx={{ fontFamily: 'monospace', letterSpacing: 1, mb: 0.5 }}>
            {code}
          </Typography>
        ))}
      </Box>
      <Button type="button" variant="outlined" color="primary" fullWidth sx={{ mb: 1 }} onClick={copyRecoveryCodes}>
        Copy Recovery Codes
      </Button>
      <Button type="submit" variant="contained" color="primary" fullWidth style={{ height: '40px' }}>
        I HAVE SAVED THESE CODES
      </Button>
    </>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#ffffff', padding: '24px' }}>
      {/* Left side */}
      <div style={{ width: '50%' }}>
        <img src={Image} alt="Illustration" style={{ width: '100%' }} />
      </div>

      {/* Right side */}
      <Paper elevation={6} style={{ padding: '40px', borderRadius: '10px', maxWidth: '500px', width: '100%', height: 'auto', transition: 'transform 0.3s' }}>
        <Box style={{ width: '100%', height: '100%' }}>
          <center><img src={logoPath} alt="Logo" style={{ width: '50%' }} /></center>
          <form onSubmit={handleFormSubmit} style={{ width: '100%', height: '100%' }}>
            {step === LOGIN_STEP && renderLoginFields()}
            {step === MFA_SETUP_STEP && renderMfaSetupFields()}
            {step === MFA_VERIFY_STEP && renderMfaVerifyFields()}
            {step === MFA_RECOVERY_STEP && renderMfaRecoveryFields()}
            {step === MFA_RECOVERY_CODES_STEP && renderRecoveryCodesFields()}
          </form>
        </Box>
      </Paper>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your email address. We will send you a link to reset your password.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="reset-email"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {loading2 ?
            <div className='p-2 mr-4'>
              <ThreeDots
                visible={true}
                height="40"
                width="40"
                color="#4fa94d"
                radius="9"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </div>
            : <Button onClick={handleSendLink} disabled={!email}>Send Reset Link</Button>}
        </DialogActions>
      </Dialog>
    </div>
  );
}
